/**
 * BD SDK logging utilities.
 *
 * @module Logger
 * @category Internal
 * @remarks
 * Provides a narrow `Logger` interface and a factory `getLogger` that adapts to the
 * runtime environment:
 *
 * - In browser-like environments, returns a console-based logger that only emits
 *   error-level messages, avoiding Node.js-only dependencies.
 * - In non-browser environments, dynamically imports `winston` and returns a fully
 *   featured logger, optionally with file rotation when enabled.
 *

 *

 *
 * It also exposes `logSdkAndDependencyVersions` to emit a single structured log with
 * version metadata helpful for debugging.
 *
 * @packageDocumentation
 */
// Importing package.json for version logging (tsconfig resolveJsonModule is enabled)
import pkgJson from "../../../package.json";
import { isLoggingDisabled } from "./configuration";
/**
 * Creates a Winston-backed logger with console output and optional file persistence with rotation.
 *
 * - Console: uses Winston defaults for formatting and levels
 * - File (optional): rotated by size (50MB) and count (10 files by default)
 *
 * Rotation uses the core Winston File transport with `maxsize` and `maxFiles`.
 * Docs: `winston` README and transports examples
 * Reference: https://github.com/winstonjs/winston
 */
/** Determine whether we are running in a browser-like environment. */
function isBrowserLikeEnv() {
    return typeof globalThis.window !== "undefined";
}
/**
 * Safely obtain a Node-style `require` function at runtime without statically
 * importing Node built-ins. This avoids bundlers (e.g., Vite) from externalizing
 * Node core modules in browser builds while still allowing Node/Bun runtimes to
 * synchronously use `require` dependencies ("node:*").
 */
function getNodeRequireSafely() {
    try {
        // Use an indirect function constructor to avoid static analysis by bundlers
        // and only resolve `require` at runtime in Node/Bun environments.
        const getReq = new Function("return (typeof require !== 'undefined' ? require : null);");
        return getReq();
    }
    catch {
        return null;
    }
}
/**
 * Create a fully silent logger that emits no output at any level.
 *
 * @remarks
 * Used by {@link getLogger} when logging is disabled via environment in non-browser
 * environments, or when a completely quiet logger is desired. In production, set
 * the environment variable `ENABLE_LOG=false` (or `0`) to force a silent logger.
 *
 * - Browser-like environments default to a console error-only logger to avoid
 *   importing Node.js dependencies.
 * - Non-browser environments can opt into full silence by configuring `ENABLE_LOG`.
 *
 * @example
 * ```bash
 * # Suppress all SDK logs in production
 * export ENABLE_LOG=false
 * ```
 *
 * @see isLoggingDisabled For the environment flag handling
 */
function createSilentLogger() {
    return {
        silly: () => { },
        debug: () => { },
        info: () => { },
        warn: () => { },
        error: () => { },
    };
}
/** Create a browser-friendly logger that only emits errors to console. */
function createConsoleErrorOnlyLogger() {
    return {
        silly: () => { },
        debug: () => { },
        info: () => { },
        warn: () => { },
        error: (message, ...meta) => {
            try {
                if (typeof console !== "undefined" && typeof console.error === "function") {
                    if (meta && meta.length)
                        console.error(message, ...meta);
                    else
                        console.error(message);
                }
            }
            catch { }
        },
    };
}
/** Build the console transport with the configured format. */
function buildConsoleTransport(winstonMod) {
    return new winstonMod.transports.Console();
}
/** Build a file transport synchronously using Node's require, handling defaults. */
function buildFileTransportSync(winstonMod, options) {
    const nodeRequire = getNodeRequireSafely();
    if (!nodeRequire) {
        throw new Error("Node-style require not available for file transport");
    }
    const path = nodeRequire("node:path");
    const fs = nodeRequire("node:fs");
    const defaultLogsDir = "logs";
    const suppliedPath = options.file?.filename;
    const derivedFilename = suppliedPath ? path.basename(suppliedPath) : `${options.label}.log`;
    const derivedDirname = suppliedPath ? path.dirname(suppliedPath) : defaultLogsDir;
    // Best-effort ensure directory and file exist
    try {
        if (!fs.existsSync(derivedDirname)) {
            fs.mkdirSync(derivedDirname, { recursive: true });
        }
        const fullPath = path.join(derivedDirname, derivedFilename);
        if (!fs.existsSync(fullPath)) {
            try {
                fs.writeFileSync(fullPath, "");
            }
            catch (innerErr) {
                console.error(`[bd-sdk] Failed to create log file ${fullPath}:`, innerErr);
            }
        }
    }
    catch (outerErr) {
        console.error(`[bd-sdk] Failed to initialize log directory ${derivedDirname}:`, outerErr);
    }
    const maxSize = options.file?.maxSizeBytes ?? 50 * 1024 * 1024;
    const maxFiles = options.file?.maxFiles ?? 10;
    return new winstonMod.transports.File({
        dirname: derivedDirname,
        filename: derivedFilename,
        maxsize: maxSize,
        maxFiles: maxFiles,
    });
}
/**
 * Get a configured logger instance for BD SDK modules.
 *
 * @remarks
 * Environment behavior:
 * - Browser-like environments (i.e., when `window` exists) return a console-based logger
 *   that only emits errors.
 * - When logs are disabled via environment (e.g., `ENABLE_LOG=false`) in non-browser
 *   environments, a fully silent logger is returned.
 * - A `createSilentLogger` helper is used internally when logging is disabled.
 *
 * When file persistence is enabled (via `options.file.enabled` or the legacy
 * `persistLogs` flag), a size-rotated file transport is added alongside console output.
 */
export function getLogger(options, persistLogs = false) {
    const level = String(options.level || "info").toLowerCase();
    // Browser-like: default to console error-only logging
    if (isBrowserLikeEnv()) {
        return createConsoleErrorOnlyLogger();
    }
    // If ENABLE_LOG is set to false, return a fully silent logger
    if (isLoggingDisabled()) {
        return createSilentLogger();
    }
    // Non-browser: synchronously load Winston via Node's require
    try {
        const nodeRequire = getNodeRequireSafely();
        if (!nodeRequire) {
            return createConsoleErrorOnlyLogger();
        }
        const winstonMod = nodeRequire("winston");
        const transports = [buildConsoleTransport(winstonMod)];
        const fileEnabled = options.file?.enabled ?? persistLogs;
        if (fileEnabled) {
            try {
                const fileTransport = buildFileTransportSync(winstonMod, options);
                transports.push(fileTransport);
            }
            catch { }
        }
        const base = winstonMod.createLogger({
            level: level,
            silent: false,
            defaultMeta: { label: options.label },
            format: winstonMod.format.combine(winstonMod.format.timestamp()),
            transports: transports,
        });
        return base;
    }
    catch {
        return createConsoleErrorOnlyLogger();
    }
}
/**
 * Log SDK and selected dependency versions using the provided logger.
 *
 * Emits a single info-level log with a structured metadata payload including
 * the SDK version and versions of important dependencies used internally.
 */
export function logSdkAndDependencyVersions(logger) {
    try {
        const version = pkgJson.version || "unknown";
        const deps = pkgJson.dependencies || {};
        const importantDeps = {
            "@blockdaemon/defi-api-sdk": deps["@blockdaemon/defi-api-sdk"],
            "@stakewise/v3-sdk": deps["@stakewise/v3-sdk"],
            axios: deps.axios,
            ethers: deps.ethers,
            winston: deps.winston,
            typescript: deps.typescript,
        };
        logger.info("BD SDK initialized", {
            "bd-sdk": { version },
            dependencies: importantDeps,
        });
    }
    catch (error) {
        logger.error("Failed to log SDK and dependency versions:", error);
    }
}
//# sourceMappingURL=data:application/json;base64,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